import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { uploadFile } from "@app/store/offersApi";
import { AppDispatch } from "@app/store/store";
import { useUpdatePublicProfileCompanyMutation } from "@entities/profile";
import { PlusIcon } from "@shared/ui/Icon/ui/Common/PlusIcon";
import { TextInput } from "@shared/ui/inputs/text-input";
import { SpriteIcon } from "@shared/ui/SpriteIcon";
import { UploadBannerFormProps } from "./types";
import classes from "./upload-banner-form.module.scss";

export const UploadBannerForm = ({ onClose }: UploadBannerFormProps) => {
    const [file, setFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [urlInput, setUrlInput] = useState<string>("");
    const [urlErrorVisible, setUrlErrorVisible] = useState<boolean>(false);

    const dispatch = useDispatch<AppDispatch>();
    const [updatePublicProfileCompany] = useUpdatePublicProfileCompanyMutation();

    const onDrop = (acceptedFiles: File[]) => {
        const droppedFile = acceptedFiles[0];
        if (droppedFile) {
            setFile(droppedFile);
            setImagePreview(URL.createObjectURL(droppedFile));
            setUrlInput("");
            setUrlErrorVisible(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (file) {
                const formData = new FormData();
                formData.append("file", file, "banner.jpg");

                const { data } = await dispatch(uploadFile(formData)).unwrap();
                const uploadedUrl = data?.data?.big;

                if (!uploadedUrl) {
                    setUrlErrorVisible(true);
                    return;
                }

                await updatePublicProfileCompany({ bannerImageUrl: uploadedUrl });
                onClose();
                return;
            }

            if (!file && !urlInput.trim()) {
                await updatePublicProfileCompany({ bannerImageUrl: "" });
                onClose();
                return;
            }

            setUrlErrorVisible(true);
        } catch {
            setUrlErrorVisible(true);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": []
        }
    });

    return (
        <div className={classes["upload-banner"]}>
            <button className={classes["upload-banner__close"]} onClick={onClose}>
                <SpriteIcon
                    className={classes["upload-banner__close--icon"]}
                    section={"common"}
                    name={"close"}
                />
            </button>
            <p className={classes["upload-banner__heading"]}>
                Перетащите изображение в эту область
            </p>
            <div
                {...getRootProps()}
                className={classNames(classes["upload-banner__dropzone"], {
                    [classes["upload-banner__dropzone--active"]]: isDragActive
                })}
            >
                <input {...getInputProps()} />
                {imagePreview ? (
                    <img
                        src={imagePreview}
                        alt="preview"
                        className={classes["upload-banner__preview"]}
                    />
                ) : (
                    <div className={classes["upload-banner__wrapper"]}>
                        <span className={classes["upload-banner__text"]}>или</span>
                        <button className={classes["upload-banner__upload"]}>
                            <PlusIcon />
                            Загрузить
                        </button>
                    </div>
                )}
            </div>
            <div className={classes["upload-banner__field"]}>
                <label className={classes["upload-banner__label"]} htmlFor="url">
                    Вставьте ссылку на изображение
                </label>
                <TextInput
                    className={classes["upload-banner__input"]}
                    id="url"
                    placeholder="Ссылка"
                    type="text"
                    value={urlInput}
                    onChange={(value) => {
                        setUrlInput(value?.toString() ?? "");
                        setUrlErrorVisible(false);
                    }}
                />
                {urlErrorVisible && (
                    <p className={classes["upload-banner__error"]}>
                        Не удалось загрузить изображение по ссылке. Попробуйте загрузить изображение
                        со своего устройства.
                    </p>
                )}
            </div>
            <button className={classes["upload-banner__submit"]} onClick={handleSubmit}>
                Сохранить
            </button>
        </div>
    );
};